import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Link, Tag, CheckCircle, XCircle, Clock } from 'lucide-react';
import { VideoSource, VideoProcessingParams, TaskStatus, TaskStep } from '../types';
import { apiService } from '../services/api';
import { useTaskStore } from '../store/taskStore';
import { validateVideoFile } from '../utils/videoHelpers';
import clsx from 'clsx';

export function TaskSubmit() {
  const { addTask } = useTaskStore();
  const [source, setSource] = useState<VideoSource>(VideoSource.USER_UPLOAD);
  const [url, setUrl] = useState('');
  const [keywords, setKeywords] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'video/*': ['.mp4', '.webm', '.ogg', '.mov', '.avi'],
    },
    maxFiles: 1,
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        setError('Invalid file format. Please upload a video file.');
        return;
      }

      const selectedFile = acceptedFiles[0];
      const validation = validateVideoFile(selectedFile);

      if (!validation.valid) {
        setError(validation.error || 'Invalid file');
        return;
      }

      setFile(selectedFile);
      setError('');
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    try {
      // 验证输入
      if (source === VideoSource.USER_UPLOAD && !file) {
        throw new Error('Please select a video file');
      }

      if (source === VideoSource.URL_IMPORT && !url) {
        throw new Error('Please enter a video URL');
      }

      if (source === VideoSource.DOUYIN && !keywords) {
        throw new Error('Please enter keywords for Douyin search');
      }

      // 准备参数
      const params: VideoProcessingParams = {
        source,
        sourceUrl: url || undefined,
        sourceKeywords: keywords ? keywords.split(',').map(k => k.trim()) : undefined,
        targetLanguage: 'ar-AE',
        enableSubtitles: true,
        enableTTS: true,
      };

      // 如果是用户上传，需要先上传文件
      if (source === VideoSource.USER_UPLOAD && file) {
        // TODO: 实现文件上传逻辑
        // 这里暂时跳过文件上传，直接提交任务
      }

      // 提交任务
      const response = await apiService.submitTask(params);

      if (response.success && response.data) {
        // 添加到本地状态
        addTask({
          status: response.data.status as TaskStatus,
          currentStep: 'fetching_video' as TaskStep,
          progress: 0,
          source,
          sourceUrl: url,
          sourceKeywords: keywords ? keywords.split(',').map(k => k.trim()) : undefined,
        });

        setSuccess('Task submitted successfully!');
        // 重置表单
        setFile(null);
        setUrl('');
        setKeywords('');
      } else {
        throw new Error(response.error || 'Failed to submit task');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit task');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Submit New Task</h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* 视频来源选择 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Video Source
          </label>
          <div className="grid grid-cols-3 gap-3">
            {[
              { value: VideoSource.USER_UPLOAD, label: 'Upload File', icon: Upload },
              { value: VideoSource.URL_IMPORT, label: 'Import URL', icon: Link },
              { value: VideoSource.DOUYIN, label: 'Douyin', icon: Tag },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setSource(option.value as VideoSource)}
                className={clsx(
                  'flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all',
                  source === option.value
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                )}
              >
                <option.icon className="w-6 h-6 mb-2" />
                <span className="text-sm font-medium">{option.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* 文件上传 */}
        {source === VideoSource.USER_UPLOAD && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Video File
            </label>
            <div
              {...getRootProps()}
              className={clsx(
                'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all',
                isDragActive
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              )}
            >
              <input {...getInputProps()} />
              <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              {file ? (
                <div>
                  <p className="text-lg font-medium text-gray-700">{file.name}</p>
                  <p className="text-sm text-gray-500">
                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-lg font-medium text-gray-700">
                    {isDragActive ? 'Drop the video here' : 'Drag & drop video here'}
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    or click to browse (MP4, WebM, MOV, AVI)
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* URL 导入 */}
        {source === VideoSource.URL_IMPORT && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Video URL
            </label>
            <div className="relative">
              <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/video.mp4"
                className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* 抖音搜索 */}
        {source === VideoSource.DOUYIN && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Keywords (comma separated)
            </label>
            <div className="relative">
              <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="电钻, 切割机, 电动螺丝刀"
                className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Enter keywords to search for popular videos on Douyin
            </p>
          </div>
        )}

        {/* 错误和成功消息 */}
        {error && (
          <div className="flex items-center p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <XCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="flex items-center p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            <span>{success}</span>
          </div>
        )}

        {/* 提交按钮 */}
        <button
          type="submit"
          disabled={isSubmitting}
          className={clsx(
            'w-full py-3 px-4 rounded-lg font-medium text-white transition-all',
            isSubmitting
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
          )}
        >
          {isSubmitting ? (
            <div className="flex items-center justify-center">
              <Clock className="w-5 h-5 mr-2 animate-spin" />
              Submitting...
            </div>
          ) : (
            'Submit Task'
          )}
        </button>
      </form>
    </div>
  );
}